import { openDB } from 'idb';

export interface GameProgress {
  version: 1;
  coins: number;
  stars: number;
  collected: string[];
  missionStars: Record<string, number>;
  unlockedVehicles: string[];
}

export const DEFAULT_PROGRESS: GameProgress = {
  version: 1,
  coins: 0,
  stars: 0,
  collected: [],
  missionStars: {},
  unlockedVehicles: ['stadtflitzer', 'familien-suv', 'kompaktbagger'],
};

const DB_NAME = 'motorwelt-save';
const STORE_NAME = 'progress';
const SAVE_KEY = 'main';
const FALLBACK_KEY = 'motorwelt.progress.v1';
const FALLBACK_ACTIVE_KEY = 'motorwelt.progress.fallback-active.v1';
let databasePromise: ReturnType<typeof openDB> | null = null;
let saveQueue: Promise<void> = Promise.resolve();

function sanitize(value: unknown): GameProgress {
  if (!value || typeof value !== 'object') return structuredClone(DEFAULT_PROGRESS);
  const data = value as Partial<GameProgress>;
  return {
    version: 1,
    coins: Math.max(0, Number(data.coins) || 0),
    stars: Math.max(0, Number(data.stars) || 0),
    collected: Array.isArray(data.collected) ? data.collected.filter((v): v is string => typeof v === 'string') : [],
    missionStars: data.missionStars && typeof data.missionStars === 'object' ? data.missionStars : {},
    unlockedVehicles: Array.isArray(data.unlockedVehicles)
      ? data.unlockedVehicles.filter((v): v is string => typeof v === 'string')
      : [...DEFAULT_PROGRESS.unlockedVehicles],
  };
}

function database(): ReturnType<typeof openDB> {
  if (!databasePromise) {
    databasePromise = openDB(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
      },
      blocking() {
        closeDatabaseConnection();
      },
      terminated() {
        databasePromise = null;
      },
    }).catch((error: unknown) => {
      // Ein spaeterer Speicherversuch darf IndexedDB erneut oeffnen, falls
      // der Browser die Datenbank nur voruebergehend blockiert hatte.
      databasePromise = null;
      throw error;
    });
  }
  return databasePromise;
}

function closeDatabaseConnection(): void {
  const current = databasePromise;
  databasePromise = null;
  if (current) void current.then((db) => db.close()).catch(() => undefined);
}

export async function loadProgress(): Promise<GameProgress> {
  // Falls der letzte IndexedDB-Schreibvorgang scheiterte, ist der explizit
  // markierte Fallback neuer als ein eventuell noch vorhandener DB-Stand.
  const activeFallback = loadFallback(true);
  if (activeFallback) return activeFallback;
  try {
    const db = await database();
    const stored = await db.get(STORE_NAME, SAVE_KEY);
    if (stored) return sanitize(stored);
  } catch {
    // Manche privaten Browsermodi sperren IndexedDB; darunter folgt der Fallback.
  }
  return loadFallback(false) ?? structuredClone(DEFAULT_PROGRESS);
}

/**
 * Speichert in Aufrufreihenfolge, damit schnelle Belohnungen keinen neueren
 * Stand durch einen spaeter fertig werdenden alten Schreibvorgang ersetzen.
 * `false` bedeutet, dass sowohl IndexedDB als auch localStorage gesperrt sind.
 */
export function saveProgress(progress: GameProgress): Promise<boolean> {
  const safe = sanitize(progress);
  const operation = saveQueue.then(() => persistProgress(safe));
  saveQueue = operation.then(
    () => undefined,
    () => undefined,
  );
  return operation;
}

async function persistProgress(safe: GameProgress): Promise<boolean> {
  try {
    const db = await database();
    await db.put(STORE_NAME, safe, SAVE_KEY);
    try {
      // Spiegel aktuell halten, damit ein spaeter zeitweise gesperrtes
      // IndexedDB nicht auf einen veralteten Fallback zurueckfaellt.
      localStorage.setItem(FALLBACK_KEY, JSON.stringify(safe));
    } catch {
      // IndexedDB ist bereits erfolgreich; der Spiegel ist optional.
    }
    try {
      localStorage.removeItem(FALLBACK_ACTIVE_KEY);
    } catch {
      // IndexedDB enthaelt bereits den aktuellen Stand; Markerbereinigung ist optional.
    }
    return true;
  } catch {
    // IndexedDB ist z. B. in manchen privaten Browsermodi gesperrt.
  }
  try {
    localStorage.setItem(FALLBACK_KEY, JSON.stringify(safe));
    localStorage.setItem(FALLBACK_ACTIVE_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

function loadFallback(requireActiveMarker: boolean): GameProgress | null {
  try {
    if (requireActiveMarker && localStorage.getItem(FALLBACK_ACTIVE_KEY) !== '1') return null;
    const raw = localStorage.getItem(FALLBACK_KEY);
    return raw ? sanitize(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function downloadProgress(progress: GameProgress): void {
  const blob = new Blob([JSON.stringify(sanitize(progress), null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'motorwelt-spielstand.json';
  a.click();
  URL.revokeObjectURL(url);
}

export async function readProgressFile(file: File): Promise<GameProgress> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(await file.text());
  } catch {
    throw new Error('Die Datei enthält kein gültiges JSON.');
  }
  if (!isProgressImport(parsed)) {
    throw new Error('Die Datei ist kein gültiger MOTORWELT-Spielstand.');
  }
  return sanitize(parsed);
}

function isProgressImport(value: unknown): value is GameProgress {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const data = value as Record<string, unknown>;
  return (
    data['version'] === 1 &&
    typeof data['coins'] === 'number' && Number.isFinite(data['coins']) &&
    typeof data['stars'] === 'number' && Number.isFinite(data['stars']) &&
    Array.isArray(data['collected']) &&
    data['missionStars'] !== null && typeof data['missionStars'] === 'object' && !Array.isArray(data['missionStars']) &&
    Array.isArray(data['unlockedVehicles'])
  );
}
